import { motion } from "framer-motion";
import PageHero from "@/components/common/PageHero";
import PageTransition from "@/components/layout/PageTransition";
import AnimatedSection from "@/components/common/AnimatedSection";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { pageHeroImages } from "@/config/site";

const serviceDetails = [
  {
    value: "implants",
    title: "Dental Implants",
    points: [
      "Single tooth implants with natural function and aesthetics.",
      "Multiple teeth replacement and implant-supported overdentures.",
      "Digital planning for precise placement and long-term stability.",
    ],
  },
  {
    value: "prosthodontics",
    title: "Prosthodontics",
    points: [
      "Crowns and bridges for durable tooth restoration.",
      "Complete and partial dentures with comfort-focused fitting.",
      "Full-mouth rehabilitation for complex bite and wear cases.",
    ],
  },
  {
    value: "general",
    title: "General Dentistry",
    points: [
      "Root Canal Treatment (RCT) with precision workflows.",
      "Tooth reshaping and preventive oral care.",
      "Microscope-assisted professional scaling and cleaning.",
    ],
  },
  {
    value: "cosmetic",
    title: "Cosmetic Dentistry",
    points: [
      "Smile design tailored to facial proportions.",
      "Advanced restorative procedures and whitening guidance.",
      "Subtle and natural enhancement of smile aesthetics.",
    ],
  },
];

export default function ServicesPage() {
  return (
    <PageTransition testId="services-page">
      <PageHero
        title="Comprehensive Dental Services"
        subtitle="Implantology, prosthodontics, general and cosmetic dentistry under one premium care ecosystem."
        image={pageHeroImages.services}
        testId="services-page-hero"
      />

      <div className="mx-auto max-w-7xl space-y-16 px-6 py-16 md:px-10 md:py-24">
        <AnimatedSection testId="services-accordion-section">
          <h2
            className="font-[Playfair_Display] text-3xl text-[#1a1a1a] md:text-5xl"
            data-testid="services-accordion-title"
          >
            Personalized treatment plans for every smile
          </h2>

          <Accordion
            type="single"
            collapsible
            className="mt-8 rounded-2xl border border-[#f2e6c9] bg-white px-6"
            data-testid="services-main-accordion"
          >
            {serviceDetails.map((service) => (
              <AccordionItem key={service.value} value={service.value}>
                <AccordionTrigger
                  className="font-[Playfair_Display] text-2xl text-[#1a1a1a] hover:no-underline"
                  data-testid={`services-accordion-trigger-${service.value}`}
                >
                  {service.title}
                </AccordionTrigger>
                <AccordionContent data-testid={`services-accordion-content-${service.value}`}>
                  <ul className="space-y-2 pb-4">
                    {service.points.map((point) => (
                      <li
                        key={point}
                        className="font-[Manrope] text-sm text-[#555555]"
                        data-testid={`services-point-${service.value}-${point
                          .slice(0, 18)
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        • {point}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </AnimatedSection>

        <AnimatedSection testId="services-highlight-grid-section">
          <div className="grid gap-6 md:grid-cols-2">
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="rounded-2xl border border-[#f2e6c9] bg-[#fffdf8] p-7"
              data-testid="services-highlight-card-tech"
            >
              <h3
                className="font-[Playfair_Display] text-2xl text-[#1a1a1a]"
                data-testid="services-highlight-tech-title"
              >
                Technology-Driven Precision
              </h3>
              <p
                className="mt-3 font-[Manrope] text-sm text-[#555555]"
                data-testid="services-highlight-tech-text"
              >
                High-resolution diagnostics and digital workflows reduce treatment
                uncertainty while improving comfort and outcomes.
              </p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="rounded-2xl border border-[#f2e6c9] bg-[#fffdf8] p-7"
              data-testid="services-highlight-card-care"
            >
              <h3
                className="font-[Playfair_Display] text-2xl text-[#1a1a1a]"
                data-testid="services-highlight-care-title"
              >
                Comfort and Clarity at Every Step
              </h3>
              <p
                className="mt-3 font-[Manrope] text-sm text-[#555555]"
                data-testid="services-highlight-care-text"
              >
                We explain each phase clearly and create treatment timelines that
                prioritize trust, confidence, and patient convenience.
              </p>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>
    </PageTransition>
  );
}
